import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";


export const SUPPORTED_LANGUAGES = ["pt", "en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = "app-language";

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  pt: "Português",
  en: "English",
  es: "Español",
};

export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  pt: "🇧🇷",
  en: "🇺🇸",
  es: "🇪🇸",
};

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

const NAMESPACES = [
  "common",
  "nav",
  "auth",
  "dashboard",
  "championship",
  "teams",
  "players",
  "matches",
  "sumula",
  "settings",
  "financial",
  "pricing",
  "pages",
] as const;
type Namespace = (typeof NAMESPACES)[number];

/**
 * Detect the initial language synchronously (before i18next init) so we can
 * eagerly bundle only the chosen language's resources. Other languages are
 * loaded on demand when the user switches.
 */
function detectInitialLanguage(): SupportedLanguage {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      const base = stored?.split("-")[0];
      if (base && isSupportedLanguage(base)) return base;
    } catch {
      /* ignore */
    }
    const nav = (typeof navigator !== "undefined" && navigator.language) || "";
    const navBase = nav.split("-")[0];
    if (isSupportedLanguage(navBase)) return navBase;
  }
  return "pt";
}

const initialLng: SupportedLanguage = detectInitialLanguage();

/**
 * Dynamic import of all namespaces for a given language. Vite splits each
 * locale folder into its own chunk so only the active language ships on the
 * critical path.
 */
const localeLoaders: Record<SupportedLanguage, () => Promise<Record<Namespace, unknown>>> = {
  pt: async () => {
    const [common, nav, auth, dashboard, championship, teams, players, matches, sumula, settings, financial, pricing, pages] = await Promise.all([
      import("./locales/pt/common.json"),
      import("./locales/pt/nav.json"),
      import("./locales/pt/auth.json"),
      import("./locales/pt/dashboard.json"),
      import("./locales/pt/championship.json"),
      import("./locales/pt/teams.json"),
      import("./locales/pt/players.json"),
      import("./locales/pt/matches.json"),
      import("./locales/pt/sumula.json"),
      import("./locales/pt/settings.json"),
      import("./locales/pt/financial.json"),
      import("./locales/pt/pricing.json"),
      import("./locales/pt/pages.json"),
    ]);
    return { common: common.default, nav: nav.default, auth: auth.default, dashboard: dashboard.default, championship: championship.default, teams: teams.default, players: players.default, matches: matches.default, sumula: sumula.default, settings: settings.default, financial: financial.default, pricing: pricing.default, pages: pages.default };
  },
  en: async () => {
    const [common, nav, auth, dashboard, championship, teams, players, matches, sumula, settings, financial, pricing, pages] = await Promise.all([
      import("./locales/en/common.json"),
      import("./locales/en/nav.json"),
      import("./locales/en/auth.json"),
      import("./locales/en/dashboard.json"),
      import("./locales/en/championship.json"),
      import("./locales/en/teams.json"),
      import("./locales/en/players.json"),
      import("./locales/en/matches.json"),
      import("./locales/en/sumula.json"),
      import("./locales/en/settings.json"),
      import("./locales/en/financial.json"),
      import("./locales/en/pricing.json"),
      import("./locales/en/pages.json"),
    ]);
    return { common: common.default, nav: nav.default, auth: auth.default, dashboard: dashboard.default, championship: championship.default, teams: teams.default, players: players.default, matches: matches.default, sumula: sumula.default, settings: settings.default, financial: financial.default, pricing: pricing.default, pages: pages.default };
  },
  es: async () => {
    const [common, nav, auth, dashboard, championship, teams, players, matches, sumula, settings, financial, pricing, pages] = await Promise.all([
      import("./locales/es/common.json"),
      import("./locales/es/nav.json"),
      import("./locales/es/auth.json"),
      import("./locales/es/dashboard.json"),
      import("./locales/es/championship.json"),
      import("./locales/es/teams.json"),
      import("./locales/es/players.json"),
      import("./locales/es/matches.json"),
      import("./locales/es/sumula.json"),
      import("./locales/es/settings.json"),
      import("./locales/es/financial.json"),
      import("./locales/es/pricing.json"),
      import("./locales/es/pages.json"),
    ]);
    return { common: common.default, nav: nav.default, auth: auth.default, dashboard: dashboard.default, championship: championship.default, teams: teams.default, players: players.default, matches: matches.default, sumula: sumula.default, settings: settings.default, financial: financial.default, pricing: pricing.default, pages: pages.default };
  },
};

const loadedLanguages = new Set<SupportedLanguage>();
async function loadLanguage(lng: SupportedLanguage): Promise<void> {
  if (loadedLanguages.has(lng)) return;
  const bundle = await localeLoaders[lng]();
  for (const ns of NAMESPACES) {
    i18n.addResourceBundle(lng, ns, (bundle as Record<string, unknown>)[ns], true, true);
  }
  loadedLanguages.add(lng);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "pt",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    defaultNS: "common",
    ns: NAMESPACES as unknown as string[],
    lng: initialLng,
    // Resources will be added asynchronously below; render with empty bundle
    // first so the JS chunk for the main app stays small.
    partialBundledLanguages: true,
    resources: { [initialLng]: {} } as Record<string, Record<string, unknown>>,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
    returnNull: false,
  });

// Eagerly load the initial language bundle (its own JS chunk) and trigger a
// re-render once available. Falls back to PT if loading fails.
void loadLanguage(initialLng).catch((err) => {
  console.error("[i18n] Failed to load initial language", initialLng, err);
  if (initialLng !== "pt") void loadLanguage("pt");
});

// When user changes language, fetch its bundle on demand.
i18n.on("languageChanged", (lng) => {
  const base = lng?.split("-")[0];
  if (isSupportedLanguage(base)) {
    void loadLanguage(base);
  }
});

// Keep <html lang> in sync for accessibility & SEO.
if (typeof document !== "undefined") {
  const updateHtmlLang = (lng: string) => {
    const base = lng.split("-")[0];
    document.documentElement.setAttribute("lang", base);
  };
  updateHtmlLang(i18n.language);
  i18n.on("languageChanged", updateHtmlLang);
}

export default i18n;
